import { useAuth } from '../stores/auth';

const ROLE_PERMISSIONS = {
  admin: {
    canManageKeys: true,
    canManageWebhooks: true,
    canUsePlayground: true,
    canViewBilling: true,
    canManageBilling: true,
    canManageTeam: true,
    canViewAuditLog: true,
  },
  developer: {
    canManageKeys: true,
    canManageWebhooks: false,
    canUsePlayground: true,
    canViewBilling: true,
    canManageBilling: false,
    canManageTeam: false,
    canViewAuditLog: true,
  },
  viewer: {
    canManageKeys: false,
    canManageWebhooks: false,
    canUsePlayground: false,
    canViewBilling: true,
    canManageBilling: false,
    canManageTeam: false,
    canViewAuditLog: true,
  },
} as const;

type Permission = keyof typeof ROLE_PERMISSIONS['admin'];

export function usePermissions() {
  const { user } = useAuth();

  const isOwner = user?.isAccountOwner !== false;
  const isSuperAdmin = !!user?.isSuperAdmin;
  const teamRole = user?.teamRole;

  // Account owners and super admins have full access
  if (isOwner || isSuperAdmin) {
    return {
      isOwner,
      isSuperAdmin,
      isTeamMember: false,
      teamRole: null as string | null,
      canManageKeys: true,
      canManageWebhooks: true,
      canUsePlayground: true,
      canViewBilling: true,
      canManageBilling: true,
      canManageTeam: true,
      canViewAuditLog: true,
      can: (_p: Permission) => true,
    };
  }

  // Team members get role-based permissions
  const perms = teamRole ? ROLE_PERMISSIONS[teamRole] : ROLE_PERMISSIONS.viewer;

  return {
    isOwner: false,
    isSuperAdmin: false,
    isTeamMember: true,
    teamRole: teamRole || 'viewer',
    ...perms,
    can: (permission: Permission) => perms[permission],
  };
}
